import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_URL, CFCProps, SCREEN_SIZE, AUTH_ERR_MSG } from 'types';
import { selectScreenSize } from 'redux/session/selectors';
import { setRedirectPath } from 'redux/session/actions';
import { RootState } from 'redux/root.reducer';
import { GenericPageLayout } from 'layouts/genericPage.layout';
import { MachineLearning_03 as MachineLearning } from '@carbon/pictograms-react';
import { Button, TextInput, Link } from 'carbon-components-react';
import { FormError } from 'components';
import { fetchOTPCode, signInWithOTPCode } from 'redux/auth/action';
import ButtonContent from 'components/ButtonContent';
import _ from 'lodash';
import Utils from 'utilities/helper.util';
import {
  redirectResidentialEnquirerIfNeeded,
  toPendingPath
} from 'utilities/residential-redirect.util';

export const OneTimePasscode: React.FC<Props> = (props: Props) => {
  const { login, session, app } = props;
  const { currentUser, redirectPath } = session;
  const { userRole } = app;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ from?: Location }>();
  const screenSize = useSelector(selectScreenSize);
  const {
    isOTPActivated,
    data,
    error,
    isSingedInWithOTP,
    isTermsAccepted
  }: any = login;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isOTPDisabled, setGetOTPDisabled] = useState<boolean>(true);
  const [emalAddress, setEmailAddress] = useState<string>('');
  const [otpCode, setOTPCode] = useState<string>('');
  const [isSignInDisabled, setSignInDisabled] = useState<boolean>(true);
  const [isValidEmail, setValidEmail] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isRequestNewOTP, setIsRequestNewOTP] = useState<boolean>(false);
  const [cognitoUserSession, setCognitoUserSession] = useState({});
  const [emailValue, setEmailValue] = useState<string>('');

  const handleOnChange = (val: any) => {
    setEmailValue(val.target.value);
    validate(val.target.value);
  };

  const validate = (values?: any) => {
    setErrorMessage('');
    setGetOTPDisabled(true);
    if (!values) {
      setErrorMessage('Required');
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values)) {
      setErrorMessage('Invalid email address');
    } else {
      setGetOTPDisabled(false);
      setEmailAddress(values);
    }
  };

  const validateOTPCode = (values?: any) => {
    setErrorMessage('');
    setSignInDisabled(true);
    if (!_.isEmpty(values.target.value)) {
      setSignInDisabled(false);
      setOTPCode(values.target.value);
    }
  };

  const getOTPCode = () => {
    setIsSubmitting(true);
    setErrorMessage('');
    dispatch(fetchOTPCode(emalAddress));
  };

  const signInWithOTP = () => {
    setErrorMessage('');
    setIsSubmitting(true);
    setSignInDisabled(true);
    const params = {
      code: otpCode,
      cognitoUserSession: cognitoUserSession
    };
    dispatch(signInWithOTPCode(params));
  };
  useEffect(() => {
    if (isValidEmail) return;
    if (!_.isUndefined(data)) {
      setCognitoUserSession(data);
      setValidEmail(true);
      setIsSubmitting(false);
    }
  }, [dispatch, data, isValidEmail]);

  useEffect(() => {
    if (!_.isUndefined(error)) {
      error?.message === AUTH_ERR_MSG.USER_ACCEPT_TERMS &&
        isSingedInWithOTP &&
        history.push(APP_URL.AUTH_USER_TERMS);
      error?.message === AUTH_ERR_MSG.USER_SESSION_ORGANISATION &&
        isSingedInWithOTP &&
        history.push(APP_URL.AUTH_MULTI_ORGANISATION);
      if (_.isUndefined(error?.message)) {
        setSignInDisabled(error === 'timeoutexception');
        setIsRequestNewOTP(error === 'timeoutexception');

        setErrorMessage(
          error.toLowerCase() === 'timeoutexception'
            ? 'Your passcode has expired. Please request a new passcode.'
            : 'Invalid code. Please re-enter.'
        );
        setIsSubmitting(false);
      }
    } else {
      const intendedPath = redirectPath || toPendingPath(location?.state?.from);
      if (
        redirectResidentialEnquirerIfNeeded(userRole, currentUser, intendedPath)
      ) {
        return;
      }
      if (location?.state?.from) {
        const toGo = `${location?.state?.from.pathname}${location?.state?.from.search}`;
        isSingedInWithOTP && history.push(toGo);
      } else {
        isSingedInWithOTP && history.push(APP_URL.DASHBOARD);
      }
    }
  }, [
    history,
    error,
    isSingedInWithOTP,
    location,
    userRole,
    currentUser,
    redirectPath
  ]);

  useEffect(() => {
    if (!Utils.isObjectEmpty(currentUser)) {
      const showTerms =
        !isTermsAccepted && error?.message === AUTH_ERR_MSG.USER_ACCEPT_TERMS;
      if (showTerms) {
        history.push(APP_URL.AUTH_USER_TERMS);
      } else {
        if (
          redirectResidentialEnquirerIfNeeded(
            userRole,
            currentUser,
            redirectPath || toPendingPath(location?.state?.from)
          )
        ) {
          return;
        }
        if (redirectPath) {
          history.push(redirectPath);
        } else {
          if (location?.state?.from) {
            const toGo = `${location?.state?.from.pathname}${location?.state?.from.search}`;
            history.push(toGo);
          } else {
            history.push(APP_URL.DASHBOARD);
          }
        }
      }
    }
  }, [
    history,
    isTermsAccepted,
    error,
    currentUser,
    location,
    redirectPath,
    userRole
  ]);

  useEffect(() => {
    if (location?.state?.from) {
      if (location.state.from.search) {
        const params = new URLSearchParams(location.state.from.search);
        const userEmail = params.get('email');
        if (userEmail) {
          setEmailValue(userEmail.trim().replace(/\s/g, '+'));
          validate(userEmail.trim().replace(/\s/g, '+'));
        }
      }
      const toGo = `${location.state.from.pathname}${location.state.from.search}`;
      dispatch(setRedirectPath(toGo));
    }
  }, [location, dispatch]);

  const getHeader = () => {
    return isValidEmail
      ? 'Verify temporary passcode'
      : 'Request temporary passcode';
  };

  const getDescription = () => {
    return isValidEmail
      ? `Enter the temporary passcode we sent to ${emalAddress}. Temporary passcodes will only be sent if you are a registered user of BYDA`
      : 'A temporary passcode will be sent to your email address. Temporary passcodes must be used within 3 minutes.';
  };

  return (
    <div id="otp" className="otp" data-testid="otp">
      <GenericPageLayout
        pageClassName="otp--wrapper"
        isOTPActivated={isOTPActivated}
        showOTPNotification={false}
        bodyFooterIcon={
          screenSize !== SCREEN_SIZE.SM && (
            <MachineLearning className="brain--icon" />
          )
        }
        hideMenuAndLinks={
          window.self !== window.top && screenSize === SCREEN_SIZE.SM
        }
      >
        <h1 className="otp--heading">{getHeader()}</h1>
        <section className="otp--wrapper">
          <div className="otp--formFieldItem">
            {!isValidEmail && (
              <TextInput
                id="email"
                name="email"
                labelText="Your registered email address"
                disabled={isSubmitting}
                onChange={(val) => handleOnChange(val)}
                helperText={getDescription()}
                value={emailValue}
              />
            )}
            {isValidEmail && (
              <TextInput
                id="passcode"
                name="passcode"
                className="passcode"
                labelText="Temporary passcode"
                disabled={isRequestNewOTP}
                onChange={(val) => validateOTPCode(val)}
                helperText={getDescription()}
              />
            )}
            {errorMessage !== '' && <FormError>{errorMessage}</FormError>}
          </div>
          <div>
            {!isValidEmail && (
              <Button
                className="otp--btn-get-otp"
                kind="primary"
                disabled={isOTPDisabled || isSubmitting}
                onClick={getOTPCode}
              >
                <ButtonContent isLoading={isSubmitting}>
                  Get temporary passcode
                </ButtonContent>
              </Button>
            )}
            {isValidEmail && (
              <div>
                <div>
                  <Button
                    kind="primary"
                    className="otp--btn-get-signin"
                    disabled={isSignInDisabled}
                    onClick={signInWithOTP}
                  >
                    <ButtonContent isLoading={isSubmitting}>
                      Sign in
                    </ButtonContent>
                  </Button>
                </div>
                <div className="otp--other-links">
                  <Link href="/oneTimePasscode">Request a new passcode</Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </GenericPageLayout>
    </div>
  );
};

const mapStateToProps = ({ app, login, session }: RootState) => ({
  app,
  login,
  session
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector>;
OneTimePasscode.displayName = 'oneTimePasscode';

export default connector(OneTimePasscode);
